import { ChevronRightIcon, ClipboardListIcon } from "lucide-react";
import React from "react";
import {
  Collection,
  ListLayout,
  type Selection,
  Tree,
  TreeItem,
  TreeItemContent,
  useFilter,
  Virtualizer,
} from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchField, SearchInput } from "@/components/ui/search-field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tab, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { cn, useMediaQuery } from "@/components/ui/utils";
import { useAuth } from "@/features/auth/hooks";
import type { RouterOutputs } from "@/lib/types";
import { setDifference } from "@/utils/set-difference";
import { useControlled } from "@/utils/use-controlled";

type TestMenuResponse = RouterOutputs["tests"]["testMenu"];
type TTestSpec = TestMenuResponse["tests"][number];
type TPanel = TestMenuResponse["panels"][number];

type TestMenuProviderProps = {
  children: React.ReactNode;
  testType?: string | null;
  selectedTests?: Set<number>;
  orgId?: number;
  onSelectedTestsChange?: (selectedTests: Set<number>) => void;
  defaultSelectedTests?: Set<number>;
  selectedPanels?: Set<number>;
  onSelectedPanelsChange?: (selectedPanels: Set<number>) => void;
  defaultSelectedPanels?: Set<number>;
  existingPanels?: Set<number>;
  existingTests?: Set<number>;
};

type TestMenuContextValue = {
  search: string;
  setSearch: (search: string) => void;
  testType: string | null;
  setTestType: (testType: string | null) => void;
  isPending: boolean;
  isSuccess: boolean;
  allTests: TTestSpec[];
  allPanels: TPanel[];
  filteredTests: TTestSpec[];
  filteredPanels: TPanel[];
  onSelectionChange: (keys: Selection) => void;
  allowDuplicates: boolean;
  setAllowDuplicates: (allowDuplicates: boolean) => void;
  orgFilter: boolean;
  setOrgFilter: (orgFilter: boolean) => void;
  hasPerSampleSelection: boolean;
} & Required<
  Pick<
    TestMenuProviderProps,
    "selectedTests" | "selectedPanels" | "existingPanels" | "existingTests"
  >
>;

type TestMenuItem = {
  id: string;
  key: string;
  name: string;
  type: "test" | "panel" | "header";
  code?: string;
  child?: boolean;
  tests?: TestMenuItem[];
  panels?: TestMenuItem[];
};

const TestMenuContext = React.createContext({} as TestMenuContextValue);

export function TestMenuProvider(props: TestMenuProviderProps) {
  const [orgFilter, setOrgFilter] = React.useState(true);
  const [search, setSearch] = React.useState<string>("");
  const [testType, setTestType] = React.useState<string | null>(
    props.testType || null,
  );
  const [allowDuplicates, setAllowDuplicates] = React.useState(false);

  React.useEffect(() => {
    setTestType(props.testType || null);
  }, [props.testType]);

  const { allTests, allPanels, isPending, isSuccess } = useTestMenuData(
    props.orgId,
    orgFilter,
  );

  const { filteredTests, filteredPanels } = useTestMenuFiltering(
    allTests,
    allPanels,
    search,
    testType,
  );

  const { selectedTests, selectedPanels, onSelectionChange } =
    useTestMenuSelection(props, allPanels, allowDuplicates);

  // Check if any selected tests or panels are PER_SAMPLE billing mode
  const hasPerSampleSelection = React.useMemo(() => {
    // Check selected individual tests
    const selectedTestsHavePerSample = allTests.some(
      (test) => selectedTests.has(test.id) && test.billingMode === "PER_SAMPLE",
    );

    // Check selected panels
    const selectedPanelsHavePerSample = allPanels.some(
      (panel) =>
        selectedPanels.has(panel.id) && panel.billingMode === "PER_SAMPLE",
    );

    return selectedTestsHavePerSample || selectedPanelsHavePerSample;
  }, [selectedTests, selectedPanels, allTests, allPanels]);

  return (
    <TestMenuContext.Provider
      value={{
        search,
        setSearch,
        testType,
        setTestType,
        isPending,
        isSuccess,
        allTests,
        filteredTests,
        allPanels,
        filteredPanels,
        onSelectionChange,
        selectedTests,
        selectedPanels,
        existingPanels: props.existingPanels ?? new Set(),
        existingTests: props.existingTests ?? new Set(),
        allowDuplicates,
        setAllowDuplicates,
        orgFilter,
        setOrgFilter,
        hasPerSampleSelection,
      }}
    >
      {props.children}
    </TestMenuContext.Provider>
  );
}

export function TestMenu() {
  const { onSelectionChange, isPending, allowDuplicates } =
    React.useContext(TestMenuContext);
  const { items, selectedKeys, disabledKeys } = useTestMenu();

  const layout = React.useMemo(
    () =>
      new ListLayout({
        estimatedRowHeight: 32,
      }),
    [],
  );

  if (isPending) {
    return <LoadingItems />;
  }

  return (
    <Virtualizer layout={layout}>
      <Tree
        aria-label="Test menu"
        items={items}
        defaultExpandedKeys={new Set()}
        className="flex-1 overflow-auto"
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={onSelectionChange}
        disabledBehavior="all"
        disabledKeys={disabledKeys}
        escapeKeyBehavior="none"
      >
        {function renderItem(item) {
          // console.log("item", item.key);
          if (item.type === "header") {
            return (
              <TreeItem
                aria-label={item.name}
                textValue={item.name}
                hasChildItems={false}
                id={item.key}
                className={cn(
                  "flex items-center px-6 font-medium bg-accent min-h-8 text-muted-foreground",
                  "outline-hidden focus-visible:rounded-md focus-visible:ring-inset focus-visible:ring-ring focus-visible:ring-2",
                )}
              >
                <TreeItemContent>
                  <Text>{item.name}</Text>
                </TreeItemContent>
              </TreeItem>
            );
          }
          return (
            <TreeItem
              aria-label={item.name}
              textValue={`${item.name} ${item.code ?? ""}`}
              id={item.key}
              hasChildItems={
                (item.tests || []).length > 0 || (item.panels || []).length > 0
              }
              className={cn(
                "min-h-8 flex items-center justify-between outline-hidden group border-b",
                "focus-visible:rounded-md focus-visible:ring-inset focus-visible:ring-ring focus-visible:ring-2",
                "data-[hovered=true]:bg-accent-muted data-[disabled=true]:text-muted-foreground",
                "pl-[calc((var(--tree-item-level)_-_1)_*_2rem+1rem)] sm:pl-[calc((var(--tree-item-level)_-_1)_*_2rem+1.5rem)] pr-4 sm:pr-6",
              )}
            >
              <TreeItemContent>
                <div className="flex items-center justify-between gap-2 min-w-0 w-full">
                  <div
                    className="flex items-center gap-2 min-w-0 flex-1"
                    title={item.name}
                  >
                    {!item.child ? (
                      <Checkbox slot="selection" />
                    ) : (
                      <div className="size-4 pl-6" />
                    )}
                    {item.tests && item.tests.length > 0 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        slot="chevron"
                        className="size-6"
                      >
                        <ChevronRightIcon className="group-data-[expanded=true]:rotate-90" />
                      </Button>
                    )}
                    {item.type === "panel" && (
                      <ClipboardListIcon className="size-4 text-muted-foreground shrink-0" />
                    )}
                    <Text
                      className={cn(
                        "text-xs sm:text-sm",
                        item.child && "text-xs",
                      )}
                      truncate
                    >
                      {item.name}
                    </Text>
                  </div>
                  {item.code && (
                    <Text className="text-xs text-muted-foreground shrink-0">
                      {item.code}
                    </Text>
                  )}
                </div>
              </TreeItemContent>
              <Collection items={item.panels ?? []}>{renderItem}</Collection>
              <Collection items={item.tests ?? []}>{renderItem}</Collection>
            </TreeItem>
          );
        }}
      </Tree>
    </Virtualizer>
  );
}

function useTestMenu() {
  const {
    filteredTests,
    filteredPanels,
    allTests,
    allPanels,
    selectedTests,
    selectedPanels,
    existingPanels = new Set<number>(),
    existingTests = new Set<number>(),
    allowDuplicates,
  } = React.useContext(TestMenuContext);

  const allItems: TestMenuItem[] = React.useMemo(
    () => [
      ...allPanels.map((panel) => toPanelItem(panel)),
      ...allTests.map((test) => toTestItem(test)),
    ],
    [allPanels, allTests],
  );

  const filteredItems: TestMenuItem[] = React.useMemo(
    () => [
      {
        id: "panels-header",
        key: "panels-header",
        name: "Panels",
        type: "header" as const,
      },
      ...filteredPanels.map((panel) => toPanelItem(panel)),
      {
        id: "tests-header",
        key: "tests-header",
        name: "Tests",
        type: "header" as const,
      },
      ...filteredTests.map((test) => toTestItem(test)),
    ],
    [filteredTests, filteredPanels],
  );

  // a map of test ids to all panel keys that include that test
  const testToPanelMap = React.useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const item of allItems) {
      if (item.type !== "panel") continue;
      const keys = getAllKeys(item);
      for (const key of keys) {
        if (!key.startsWith("test-")) continue;
        const set = map.get(key) ?? new Set<string>();
        set.add(item.key);
        map.set(key, set);
      }
    }
    return map;
  }, [allItems]);

  // the set of all existing keys
  const existingKeys = React.useMemo(() => {
    const keys = new Set<string>();
    for (const test of existingTests) keys.add(`test-${test}`);
    for (const panel of existingPanels) keys.add(`panel-${panel}`);
    return keys;
  }, [existingTests, existingPanels]);

  // the set of all selected keys
  const selectedKeys = React.useMemo(() => {
    const keys = new Set<string>();
    for (const test of selectedTests ?? []) keys.add(`test-${test}`);
    for (const panel of selectedPanels ?? []) keys.add(`panel-${panel}`);

    // Only show existing keys as selected if duplicates are not allowed
    if (!allowDuplicates) {
      for (const key of existingKeys) {
        keys.add(key);
      }
    }

    return keys;
  }, [selectedTests, selectedPanels, existingKeys, allowDuplicates]);

  const disabledKeys = React.useMemo(() => {
    // Always disable headers
    const keys = new Set<string>(["panels-header", "tests-header"]);

    // If duplicates are allowed, don't disable anything else
    if (allowDuplicates) {
      return keys;
    }

    // If duplicates are not allowed, apply the original disabling logic
    for (const item of allItems) {
      if (item.type === "header") continue;

      if (
        existingKeys.has(item.id) ||
        (selectedKeys.has(item.id) && item.type === "panel")
      ) {
        for (const key of getAllKeys(item)) {
          keys.add(key);
        }
      }
    }

    // disable panels that include selected tests
    for (const key of keys) {
      const panelKeys = testToPanelMap.get(key) ?? new Set<string>();
      for (const panelKey of panelKeys) {
        keys.add(panelKey);
      }
    }

    // never disable selected keys (except existing ones)
    for (const key of setDifference(selectedKeys, existingKeys)) {
      keys.delete(key);
    }

    return keys;
  }, [selectedKeys, existingKeys, testToPanelMap, allItems, allowDuplicates]);

  return { items: filteredItems, selectedKeys, disabledKeys };
}

function getAllKeys(item: TestMenuItem): Set<string> {
  const keys = new Set<string>();
  keys.add(item.id);

  for (const panel of item.panels ?? []) {
    for (const key of getAllKeys(panel)) {
      keys.add(key);
    }
  }

  for (const test of item.tests ?? []) {
    for (const key of getAllKeys(test)) {
      keys.add(key);
    }
  }

  return keys;
}

function toPanelItem(panel: TPanel, parent?: TPanel): TestMenuItem {
  const key = parent
    ? `child-panel-${parent.id}-panel-${panel.id}`
    : `panel-${panel.id}`;

  return {
    key,
    id: `panel-${panel.id}`,
    name: panel.name,
    type: "panel",
    child: !!parent,
    panels: panel.panels?.map((p) => toPanelItem(p as TPanel, panel)),
    tests: panel.tests.map((t) => toTestItem(t, parent ?? panel)),
  };
}

function toTestItem(test: TTestSpec, parent?: TPanel): TestMenuItem {
  const key = parent
    ? `child-panel-${parent.id}-test-${test.id}`
    : `test-${test.id}`;

  return {
    key,
    id: `test-${test.id}`,
    name: test.name,
    child: !!parent,
    type: "test",
    code: test.code,
  };
}

export function TestMenuSearch({ className }: { className?: string }) {
  const { search, setSearch } = React.useContext(TestMenuContext);

  return (
    <SearchField
      aria-label="Search"
      value={search}
      onChange={setSearch}
      className={className}
    >
      <SearchInput />
    </SearchField>
  );
}

const testTypes = [
  { id: "SEROLOGY", name: "Serology" },
  { id: "CLINICAL", name: "Clinical" },
  { id: "HLA", name: "HLA" },
  { id: "MICROBIOLOGY", name: "Microbiology" },
];

export function TestMenuTestTypeSelect({
  isDisabled,
  className,
}: {
  isDisabled?: boolean;
  className?: string;
}) {
  const {
    testType,
    setTestType,
    selectedTests,
    selectedPanels,
    allTests,
    allPanels,
  } = React.useContext(TestMenuContext);

  // used to only display the test types that are available
  const availableTestTypes = React.useMemo(() => {
    const types = new Set<string>();
    for (const test of allTests) types.add(test.type);
    for (const panel of allPanels) {
      for (const test of panel.tests) {
        types.add(test.type);
      }
    }
    return types;
  }, [allTests, allPanels]);

  const testTypeOptions = React.useMemo(() => {
    return testTypes.filter((type) => availableTestTypes.has(type.id));
  }, [availableTestTypes]);

  // if there are selected tests, set the test type to the first test type
  React.useEffect(() => {
    const tests = allTests.filter((test) => selectedTests.has(test.id));
    const panels = allPanels.filter((panel) => selectedPanels.has(panel.id));
    if (tests.length > 0) {
      setTestType(tests[0].type);
    } else if (panels.length > 0 && panels[0].tests.length > 0) {
      setTestType(panels[0].tests[0].type);
    }
  }, [selectedTests, selectedPanels, allTests, allPanels, setTestType]);

  const isSelectDisabled =
    isDisabled || selectedTests.size > 0 || selectedPanels.size > 0;

  const isMobile = useMediaQuery("(max-width: 768px)");

  return isMobile ? (
    <Select
      className={cn("w-36", className)}
      placeholder="Test type"
      aria-label="Test type"
      selectedKey={testType}
      onSelectionChange={(value) => setTestType(value as string)}
      isDisabled={isSelectDisabled}
    >
      <SelectTrigger />
      <SelectList>
        {testTypeOptions.map((type) => (
          <SelectItem id={type.id} key={type.id}>
            {type.name}
          </SelectItem>
        ))}
      </SelectList>
    </Select>
  ) : (
    <Tabs
      selectedKey={testType}
      onSelectionChange={(value) => setTestType(value as string)}
      isDisabled={isSelectDisabled}
      variant="segment"
    >
      <TabsList>
        {testTypeOptions.map((type) => (
          <Tab id={type.id} key={type.id}>
            {type.name}
          </Tab>
        ))}
      </TabsList>
    </Tabs>
  );
}

export function TestMenuAllowDuplicatesSwitch({
  className,
}: {
  className?: string;
}) {
  const { allowDuplicates, setAllowDuplicates } =
    React.useContext(TestMenuContext);

  return (
    <Switch
      isSelected={allowDuplicates}
      onChange={setAllowDuplicates}
      className={className}
    >
      <Text className="text-sm text-muted-foreground">Allow duplicates</Text>
    </Switch>
  );
}

function LoadingItems() {
  return (
    <div className="flex flex-col divide-y">
      {new Array(20).fill(null).map((_, i) => (
        <div key={i} className="h-8 flex items-center px-6 gap-2">
          <Skeleton className="size-5" />
          <Skeleton className="size-5" />
          <Skeleton className="h-5 w-48" />
        </div>
      ))}
    </div>
  );
}

export function TestMenuOrgFilterSwitch({ className }: { className?: string }) {
  const { orgFilter, setOrgFilter } = React.useContext(TestMenuContext);
  const { user } = useAuth();

  if (!user?.isLabRole()) return null;

  return (
    <Switch
      isSelected={!orgFilter}
      onChange={(val) => setOrgFilter(!val)}
      className={className}
    >
      <Text className="text-sm text-muted-foreground">View all</Text>
    </Switch>
  );
}

export function useTestMenuContext() {
  return React.useContext(TestMenuContext);
}

// Custom hook for data fetching and processing
function useTestMenuData(orgId?: number, orgFilter?: boolean) {
  const {
    data: testMenuData,
    isPending,
    isSuccess,
  } = trpc.tests.testMenu.useQuery(
    { orgId, orgFilter },
    {
      enabled: !Number.isNaN(orgId),
      trpc: { context: { skipBatch: true } },
    },
  );

  const { tests: allTests, panels: allPanels } = testMenuData ?? {
    tests: [],
    panels: [],
  };

  return {
    allTests: allTests ?? [],
    allPanels: allPanels ?? [],
    isPending,
    isSuccess,
  };
}

// Custom hook for filtering logic
function useTestMenuFiltering(
  allTests: TTestSpec[],
  allPanels: TPanel[],
  search: string,
  testType: string | null,
) {
  const { contains } = useFilter({ sensitivity: "base", usage: "search" });

  const filteredTests = React.useMemo(() => {
    return allTests?.filter(
      (test) =>
        test.type === testType &&
        (contains(test.name, search) || contains(test.code, search)),
    );
  }, [allTests, search, contains, testType]);

  const filteredPanels = React.useMemo(() => {
    return allPanels?.filter(
      (panel) =>
        panel.tests.some((test) => test.type === testType) &&
        (contains(panel.name, search) ||
          panel.tests.some((test) => contains(test.name, search)) ||
          panel.panels.some(
            (p) =>
              contains(p.name, search) ||
              p.tests.some((t) => contains(t.name, search)),
          )),
    );
  }, [allPanels, search, contains, testType]);

  return { filteredTests, filteredPanels };
}

// Custom hook for selection management
function useTestMenuSelection(
  props: TestMenuProviderProps,
  allPanels: TPanel[],
  allowDuplicates: boolean,
) {
  const {
    selectedTests: selectedTestsProp,
    defaultSelectedTests,
    onSelectedTestsChange,
    selectedPanels: selectedPanelsProp,
    defaultSelectedPanels,
    onSelectedPanelsChange,
  } = props;

  const [selectedTests, setSelectedTests] = useControlled({
    controlled: selectedTestsProp,
    default: defaultSelectedTests,
    name: "TestMenu",
    state: "selectedTests",
  });

  const [selectedPanels, setSelectedPanels] = useControlled({
    controlled: selectedPanelsProp,
    default: defaultSelectedPanels,
    name: "TestMenu",
    state: "selectedPanels",
  });

  const onSelectionChange = React.useCallback(
    (keys: Selection) => {
      if (keys === "all") return;

      const tests = new Set<number>();
      const panels = new Set<number>();

      for (const key of keys) {
        if (typeof key === "string" && key.startsWith("test-")) {
          const testId = Number(key.split("-")[1]);
          // Add test if duplicates are allowed OR if it's not an existing test
          if (allowDuplicates || !props.existingTests?.has(testId)) {
            tests.add(testId);
          }
        } else if (typeof key === "string" && key.startsWith("panel-")) {
          const panelId = Number(key.split("-")[1]);
          // Add panel if duplicates are allowed OR if it's not an existing panel
          if (allowDuplicates || !props.existingPanels?.has(panelId)) {
            panels.add(panelId);
          }
        }
      }

      let finalTests = tests;

      // Only apply panel/test conflict logic if duplicates are NOT allowed
      if (!allowDuplicates) {
        const testsInPanels = new Set<number>();
        for (const panel of allPanels ?? []) {
          if (!panels.has(panel.id)) continue;
          for (const test of panel.tests) {
            testsInPanels.add(test.id);
          }
          for (const child of panel.panels ?? []) {
            for (const test of child.tests) {
              testsInPanels.add(test.id);
            }
          }
        }
        finalTests = setDifference(tests, testsInPanels);
      }

      onSelectedTestsChange?.(finalTests);
      setSelectedTests(finalTests);
      onSelectedPanelsChange?.(panels);
      setSelectedPanels(panels);
    },
    [
      onSelectedTestsChange,
      onSelectedPanelsChange,
      setSelectedTests,
      setSelectedPanels,
      allPanels,
      props.existingTests,
      props.existingPanels,
      allowDuplicates,
    ],
  );

  return {
    selectedTests: selectedTests ?? new Set(),
    selectedPanels: selectedPanels ?? new Set(),
    onSelectionChange,
  };
}
